import { useEffect, useState, useSyncExternalStore } from 'react';
import { apiJson, isMissingRoute } from '../api/client';
import { ApiError } from '../api/errors';

/*
 * Встановлюваний застосунок (PWA): реєстрація сервіс-воркера (лише в збірці), подія «Встановити застосунок»,
 * підказка для iOS, черга офлайн-дій і Web Push. Подій сервіс-воркера оболонка слухає через subscribePwa().
 */

type InstallEvent = Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }> };

let deferred: InstallEvent | null = null;
let installed = false;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

export type PwaMessage =
  | { type: 'ers:queued'; url: string }
  | { type: 'ers:replayed'; ok: boolean; status: number; url: string; detail?: string }
  | { type: 'ers:queue'; size: number; sent: number }
  | { type: 'ers:navigate'; url: string }
  | { type: 'ers:sw-activated'; version: string }
  /** Особа, яку сервіс-воркер підтвердив на сервері: вхід, вихід або завершений сеанс (в усіх вкладках). */
  | { type: 'ers:identity'; state: 'signed-in' | 'signed-out' | 'locked'; userId: string | null; orgId: string | null }
  /** Відкладені дії вилучено без надсилання: інший працівник, вихід або минув строк очікування. */
  | { type: 'ers:dropped'; count: number; reason: 'other-user' | 'signed-out' | 'expired' };

const pwaListeners = new Set<(m: PwaMessage) => void>();
export function subscribePwa(cb: (m: PwaMessage) => void) {
  pwaListeners.add(cb);
  return () => {
    pwaListeners.delete(cb);
  };
}

/** Позначка цієї збірки вебзастосунку (вона ж — версія сервіс-воркера sw.js?v=…). */
export const BUILD_ID: string = typeof __ERS_BUILD_ID__ === 'string' ? __ERS_BUILD_ID__ : 'dev';

/** Раннє підключення: до першого малювання, щоб не пропустити beforeinstallprompt. */
export function initPwa() {
  if (typeof window === 'undefined') return;
  // Вкладка лишилася від попередньої версії, а її частини на сервері вже немає: одне перезавантаження бере нову версію.
  window.addEventListener('vite:preloadError', () => {
    // Без мережі перезавантаження не допоможе — сторінка сама пояснить, що її ще не збережено.
    if (navigator.onLine === false) return;
    try {
      const key = 'ers.reloaded-for';
      if (window.sessionStorage.getItem(key) === window.location.pathname) return;
      window.sessionStorage.setItem(key, window.location.pathname);
    } catch {
      return;
    }
    window.location.reload();
  });
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferred = e as InstallEvent;
    emit();
  });
  window.addEventListener('appinstalled', () => {
    installed = true;
    deferred = null;
    emit();
  });
  if (!('serviceWorker' in navigator)) return;
  navigator.serviceWorker.addEventListener('message', (e: MessageEvent<PwaMessage>) => {
    if (e.data && typeof e.data.type === 'string') pwaListeners.forEach((l) => l(e.data));
  });
  // Сервіс-воркер — лише в збірці: у розробці він заважав би гарячому оновленню.
  if (import.meta.env.PROD) {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register(`/sw.js?v=${BUILD_ID}`, { scope: '/' }).catch(() => {});
    });
    window.addEventListener('online', () => flushOfflineQueue());
  }
}

export function flushOfflineQueue() {
  navigator.serviceWorker?.controller?.postMessage({ type: 'ers:flush' });
}

/** Повідомлення сервіс-воркеру з очікуванням відповіді (обмежено часом: воркера може не бути). */
function askWorker(message: { type: string }, timeoutMs = 2000): Promise<boolean> {
  const controller = navigator.serviceWorker?.controller;
  if (!controller || typeof MessageChannel === 'undefined') return Promise.resolve(false);
  return new Promise((resolve) => {
    const channel = new MessageChannel();
    const timer = window.setTimeout(() => resolve(false), timeoutMs);
    channel.port1.onmessage = () => {
      window.clearTimeout(timer);
      resolve(true);
    };
    controller.postMessage(message, [channel.port2]);
  });
}

/** Те саме очищення зі сторінки — на випадок, коли воркер цією вкладкою не керує (примусове перезавантаження). */
async function clearOfflineStorageFromPage() {
  if ('caches' in window) {
    for (const key of await caches.keys()) if (key.startsWith('ers-data-')) await caches.delete(key);
  }
  if (!('indexedDB' in window)) return;
  await new Promise<void>((resolve) => {
    // Відкриття сховища може чекати на інше з'єднання (оновлення версії воркером): вихід через це не зависає.
    const timer = window.setTimeout(resolve, 3000);
    const open = indexedDB.open('ers-offline');
    open.onblocked = () => resolve();
    open.onerror = () => resolve();
    open.onsuccess = () => {
      const db = open.result;
      if (!db.objectStoreNames.contains('outbox')) {
        db.close();
        resolve();
        return;
      }
      const t = db.transaction('outbox', 'readwrite');
      t.objectStore('outbox').clear();
      const done = () => {
        window.clearTimeout(timer);
        db.close();
        resolve();
      };
      t.oncomplete = done;
      t.onerror = done;
      t.onabort = done;
    };
  });
}

/**
 * Вихід: прибрати з цього браузера офлайн-дані й чергу відкладених дій.
 * Сервіс-воркер робить це сам, щойно бачить запит виходу; тут — явне доручення й дубль зі сторінки.
 */
export async function clearOfflineData(): Promise<void> {
  await Promise.allSettled([askWorker({ type: 'ers:sign-out' }), clearOfflineStorageFromPage()]);
}

export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

export function isIOS(): boolean {
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) || (ua.includes('Macintosh') && navigator.maxTouchPoints > 1);
}

/** Стан встановлення: кнопка (Chrome, Edge, Android) або підказка для iOS. */
export function useInstallPrompt() {
  const snapshot = useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => {
        listeners.delete(cb);
      };
    },
    () => `${!!deferred}|${installed}`,
  );
  const [canInstall, isInstalled] = snapshot.split('|').map((x) => x === 'true');
  return {
    canInstall: !!canInstall,
    installed: !!isInstalled || isStandalone(),
    iosHint: isIOS() && !isStandalone(),
    install: async () => {
      const e = deferred;
      if (!e) return 'unavailable' as const;
      await e.prompt();
      const choice = await e.userChoice;
      deferred = null;
      emit();
      return choice.outcome;
    },
  };
}

/** Розмір черги офлайн-дій (з повідомлень сервіс-воркера). */
export function useOfflineQueue() {
  const [size, setSize] = useState(0);
  useEffect(() => {
    const off = subscribePwa((m) => {
      if (m.type === 'ers:queue') setSize(m.size);
      if (m.type === 'ers:queued') setSize((s) => s + 1);
    });
    navigator.serviceWorker?.controller?.postMessage({ type: 'ers:queue-size' });
    return off;
  }, []);
  return size;
}

// ——— Web Push ———

export type PushState = 'unsupported' | 'denied' | 'default' | 'granted';

export function pushSupport(): PushState {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) return 'unsupported';
  return Notification.permission as PushState;
}

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

function deviceName(): string {
  const ua = navigator.userAgent;
  const browser = /Edg\//.test(ua) ? 'Edge' : /Chrome\//.test(ua) ? 'Chrome' : /Firefox\//.test(ua) ? 'Firefox' : /Safari\//.test(ua) ? 'Safari' : 'Браузер';
  const os = /Android/.test(ua) ? 'Android' : isIOS() ? 'iOS' : /Mac OS X/.test(ua) ? 'macOS' : /Windows/.test(ua) ? 'Windows' : /Linux/.test(ua) ? 'Linux' : '';
  return [browser, os].filter(Boolean).join(' · ');
}

/**
 * Увімкнути сповіщення (лише за дією користувача): дозвіл → ключ VAPID сервера → підписка → реєстрація пристрою.
 * Повертає пояснення українською для інтерфейсу.
 */
export async function enablePush(): Promise<{ ok: boolean; message: string }> {
  if (pushSupport() === 'unsupported') return { ok: false, message: 'Цей браузер не підтримує сповіщення. На iPhone спершу встановіть ЄРС на екран «Додому».' };
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return { ok: false, message: 'Сповіщення заборонено в налаштуваннях браузера. Дозвольте їх для цього сайту й спробуйте ще раз.' };
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return { ok: false, message: 'Сповіщення працюють у встановленому застосунку чи робочій збірці (сервіс-воркер не зареєстровано).' };
  let key: string;
  try {
    const res = await apiJson<{ publicKey?: string; key?: string }>('GET', '/api/v1/push/vapid-public-key');
    key = res.publicKey ?? res.key ?? '';
  } catch (e) {
    if (isMissingRoute(e) || (e instanceof ApiError && e.status === 404)) return { ok: false, message: 'Сервер ще не підтримує push-сповіщення. Спробуйте пізніше.' };
    return { ok: false, message: e instanceof ApiError ? e.detail : 'Не вдалося отримати ключ сповіщень.' };
  }
  if (!key) return { ok: false, message: 'Сервер не повернув ключ сповіщень.' };
  const sub = (await reg.pushManager.getSubscription()) ?? (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(key) as BufferSource }));
  try {
    await apiJson('POST', '/api/v1/devices', { platform: 'web', name: deviceName(), push: { kind: 'webpush', token: JSON.stringify(sub) } });
  } catch (e) {
    if (isMissingRoute(e)) return { ok: false, message: 'Сервер ще не приймає реєстрацію пристроїв. Підписку збережено в браузері.' };
    return { ok: false, message: e instanceof ApiError ? e.detail : 'Пристрій не зареєстровано.' };
  }
  return { ok: true, message: 'Сповіщення увімкнено на цьому пристрої.' };
}
